import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { WineSummary } from '@/api/wines';

export interface TastingRecord {
  id: string;
  wine: WineSummary;
  rating?: number;
  tastedAt: string;
}

export function useRecentTastings(limit = 6) {
  const { data: tastings = [], isLoading: loading } = useQuery({
    queryKey: ['recent-tastings', limit],
    queryFn: async () => {
      const { data: records } = await apiClient.models.UserWine.list({ limit });
      if (!records || records.length === 0) return [];

      const wineIds = [...new Set(records.map((r) => r.wineId))];
      const wines = await Promise.all(wineIds.map((id) => apiClient.models.Wine.get({ id })));
      const wineMap = new Map(wines.filter((r) => r.data).map((r) => [r.data!.id, r.data!]));

      return records
        .filter((r) => wineMap.has(r.wineId))
        .map((r) => {
          const w = wineMap.get(r.wineId)!;
          return {
            id: r.id,
            wine: { id: w.id, name: w.name, producer: w.producer ?? undefined, vintage: w.vintage ?? undefined, style: w.style ?? undefined },
            rating: r.rating ?? undefined,
            tastedAt: r.tastedAt,
          } satisfies TastingRecord;
        })
        .sort((a, b) => new Date(b.tastedAt).getTime() - new Date(a.tastedAt).getTime());
    },
  });

  return { tastings, loading };
}
